module OnlineDPR.Application.Client.DprAdministration

open System

open Elmish
open Fable.Core
open Fable.Core.JsInterop
open Fable.React
open Fable.React.Props
open Fable.Import

open OnlineDPR.Application.Shared
open OnlineDPR.Application.Client
open OnlineDPR.Application.Client.Components
open Fetch.Types

type Msg =
| ParseClubDataList of Result<string list, string>
| ClubSelected of Field
| DateSelected of Field
| DprNotificationRequested
| DprNotificationResponded of Result<Result<EditRequestResult, string>, exn>

let logger = Jsnlog.GetLoggerWithName "DprAdministration"

let fetchClubs() = Http.fetchAs<string list> APIUrls.GetClubNumbersWithManualDpr [ Credentials RequestCredentials.Include ]
let initClubList = Cmd.OfPromise.either fetchClubs () (ParseClubDataList) ((fun e -> Error(string e)) >> ParseClubDataList)
let init () =
    let initField key fieldType value hideLabel label =
            Field.Init key fieldType (fun f -> { f with IsMandatory = false
                                                        Value = value
                                                        HideLabel = hideLabel
                                                        Label = Some label })
    let minFromToday = DateTime.Today.AddMonths(-3)
    let maxFromToday = DateTime.Today.AddMonths(1)

    DprAdministrationModel {
        AddDprNotification = {
            ClubNumber = initField "club" FieldType.String "" false "Club-Nr."
            Date = initField "month" (FieldType.Date {
                                        Min = Some ({Year = minFromToday.Year; Month = minFromToday.Month; Day = minFromToday.Day});
                                        Max = Some ({Year = maxFromToday.Year; Month = maxFromToday.Month; Day = maxFromToday.Day})
                                      })
                                      "" false "Datum"
            ErrorMessage = None
            PendingRequest = false
        }
        ValidClubs = [] } , initClubList

let createDprNotificationRequestCmd (data:CreateDprNotificationRequest) =
    Cmd.OfPromise.either(Http.postData APIUrls.PostCreateNewDprNotification) data (Ok >> DprNotificationResponded) (Error >> DprNotificationResponded)

let validateClubNumber field validClubs fieldsMustNotBeEmpty =
    let s = validateField field
    match (s.Value, s.Invalid, Seq.contains s.Value validClubs, fieldsMustNotBeEmpty) with
    | "", _, _, false -> s
    | "", _, _, true -> {s with Invalid = true; ValidationMessage = Some (ValidationError.KeyOnly Translations.TranslationKey.FormValidation_NotAClubWithDpr) }
    | _, true, _, _ -> s
    | _, false, true, _ -> s
    | _, false, false, _ -> {s with Invalid = true; ValidationMessage = Some (ValidationError.KeyOnly Translations.TranslationKey.FormValidation_NotAClubWithDpr) }

let validateCreateDprNotificationRequest (m:EditRowModel) validClubs fieldsMustNotBeEmpty =
    { m with
        ClubNumber = validateClubNumber m.ClubNumber validClubs fieldsMustNotBeEmpty
        Date = match m.Date.Value, fieldsMustNotBeEmpty with
               | "", false -> m.Date
               | _, _ -> validateField m.Date }

let update msg model =
    match msg with
    | ParseClubDataList r -> match r with
                             | (Ok dataList) -> { model with ValidClubs = dataList}, []
                             | (Error e) ->
                                    logger.error e
                                    model, []
    | ClubSelected f -> let validatedModel = validateCreateDprNotificationRequest {model.AddDprNotification with ClubNumber = f; ErrorMessage = None} model.ValidClubs false
                        { model with AddDprNotification = validatedModel }, [ ]
    | DateSelected f -> let validatedModel = validateCreateDprNotificationRequest {model.AddDprNotification with Date = f; ErrorMessage = None} model.ValidClubs false
                        { model with AddDprNotification = validatedModel }, [ ]
    | DprNotificationRequested -> let model =  { model with AddDprNotification = validateCreateDprNotificationRequest model.AddDprNotification model.ValidClubs true }
                                  let validationMessage = List.tryFind (fun x -> Option.isSome x.ValidationMessage) [ model.AddDprNotification.ClubNumber; model.AddDprNotification.Date]
                                  match validationMessage with
                                  | Some _ -> model, []
                                  | None -> { model with AddDprNotification = { model.AddDprNotification with PendingRequest = true } },
                                            let parsedDate = DateTime.Parse model.AddDprNotification.Date.Value        
                                            (createDprNotificationRequestCmd {
                                                ClubNumber = model.AddDprNotification.ClubNumber.Value;
                                                SelectedDay = {
                                                    Year = parsedDate.Year;
                                                    Month = parsedDate.Month;
                                                    Day = parsedDate.Day } })
    | DprNotificationResponded r ->  match r with
                                     | Ok ( Ok (EditRequestSuccessMessage r)) ->
                                                            { model with AddDprNotification = { model.AddDprNotification with ErrorMessage = Some r} } , []
                                     | Ok ( Ok (EditRequestError s)) ->
                                                            { model with AddDprNotification = { model.AddDprNotification with ErrorMessage = Some s } } , []
                                     | Ok ( Ok _ ) -> model, []
                                     | Ok ( Error s) ->
                                         logger.error s
                                         model, []
                                     | Error e ->
                                         logger.error e
                                         model, []

let errorMessage s =
    s
    |> List.map (fun t -> [ span [ Class "has-error" ] [ str t] ] |> ofList )

let renderError (m:EditRowModel) =
    match m.ClubNumber.ValidationMessage, m.Date.ValidationMessage, m.ErrorMessage with
    | Some c, Some d, _ -> [ validationMessage c; validationMessage d] |> errorMessage
    | Some c, None, _ -> [ validationMessage c ] |> errorMessage
    | None, Some d, _ -> [ validationMessage d ] |> errorMessage
    | None, None, Some s -> errorMessage [s]
    | None, None, None -> []
    |> ofList

let view model dispatch =
    article [ Class "content" ]
            [ div [ Class "title-block" ]
                  [ h3 [ Class "title" ]
                       [ str "DPR Verwaltung" ] ]
              section [ Class "section" ]
                      [ div [ ClassName "card card-default" ]
                            [ div [ ClassName "card-block" ]
                                  [ p [ ClassName "title" ]
                                      [ a [ Href PageUrls.dprReport ] [ str "DPR-Report" ] ] ] ]

                        div [ ClassName "card card-default" ]
                            [ div [ ClassName "card-header" ]
                                  [ div [ ClassName "header-block" ]
                                        [ p [ ClassName "title" ]
                                            [ str "DPR Notification anlegen" ] ] ]
                              div [ ClassName "card card-default card-editform" ]
                                  [ div [ ClassName "card-block" ]
                                        [ div [ ClassName "form-inline" ]
                                              [ InlineFormFieldComponent model.AddDprNotification.ClubNumber (ClubSelected >> dispatch)
                                                InlineFormFieldComponent model.AddDprNotification.Date (DateSelected >> dispatch)
                                                button [ Class "btn btn-primary"
                                                         OnClick (fun _ -> dispatch DprNotificationRequested)]
                                                       [ str "Anlegen"]
                                                div []
                                                    [ renderError model.AddDprNotification ] ] ] ] ] ] ]